import { useDraftDirty, useUnsavedChanges } from "../common/UnsavedChangesBoundary";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hasModuleAccess } from "../../accessControl";
import { RoleEnum, isRole } from "../../constants/roles";
import { useAuth } from "../../useAuth";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";
import { listRiskCategories } from "../../api/riskCategories.api";
import { listUsers } from "../../api/users.api";
import { listAdminUsers } from "../../api/admin.api";
import { getRiskMatrixConfig } from "../../api/riskMatrixConfig.api";
import {
  archiveRisk,
  deleteRisk,
  exportRiskPdf,
  getRiskById,
  getRiskHistory,
  restoreRisk,
  updateRisk,
} from "../../riskService";
import LinkedControlsPanel from "../LinkedControlsPanel.jsx";
import ControlDetailDrawer from "../../ControlDetailDrawer";
import ActionDetailDrawer from "../../ActionDetailDrawer";
import RiskActionsPanel from "../../RiskActionsPanel";
import RiskEvidence from "../../RiskEvidence";
import Button from "../../ui/Button";
import PinButton from "../pins/PinButton";
import Input from "../../ui/Input";
import ModalA11yWrapper from "../../ui/ModalA11yWrapper";
import StatusBadge from "../../ui/StatusBadge";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import RichTextEditor from "../../ui/RichTextEditor";
import CollapsibleSection from "../../ui/CollapsibleSection";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import ControlCreateModal from "../controls/ControlCreateModal";
import { useControlEffectivenessConfig } from "../../hooks/useControlEffectivenessConfig";
import {
  ensureRichTextHtml,
  isRichTextEmpty,
  richTextToPlainText,
  sanitizeRichTextHtml,
} from "../../utils/riskReviewTemplate";
import {
  getControlEffectivenessLabel,
  normalizeControlEffectivenessKey,
} from "../../utils/controlEffectiveness";
import ScoringPanel from "./ScoringPanel";
import RiskPrintModal from "./print/RiskPrintModal";
import RiskPrintPreview from "./print/RiskPrintPreview";
import RiskPrintStyles from "./print/RiskPrintStyles";
import { normalizeMatrixConfig } from "../../utils/riskMatrixLogic";
import { DEFAULT_PRINT_FIELD_KEYS, DEFAULT_PRINT_SUB_OPTIONS } from "./print/riskPrintConfig";
import {
  buildRiskPrintFieldMap,
  buildRiskPrintPreview,
  estimateRiskPrintOverflow,
} from "./print/riskPrintUtils";

const REVIEW_FREQUENCY_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Bi-Annual", value: "semiannual" },
  { label: "Annual", value: "annual" },
];

const REVIEW_FREQUENCY_LABELS = REVIEW_FREQUENCY_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const TREATMENT_DECISION_OPTIONS = [
  { label: "Mitigate", value: "mitigate" },
  { label: "Accept", value: "accept" },
  { label: "Transfer", value: "transfer" },
  { label: "Avoid", value: "avoid" },
];
const CONTROL_EFFECTIVENESS_SUMMARY_TONES = {
  ineffective: "danger",
  needs_improvement: "warning",
  effective: "success",
  unrated: "neutral",
};
const ACTION_PRIORITY_SUMMARY_ITEMS = [
  { key: "critical", label: "Critical", tone: "danger" },
  { key: "high", label: "High", tone: "warning" },
  { key: "medium", label: "Medium", tone: "info" },
  { key: "low", label: "Low", tone: "neutral" },
  { key: "unassigned", label: "Unset", tone: "neutral" },
];
const ACTION_COMPLETION_SUMMARY_ITEMS = [
  { key: "completed", label: "Completed", tone: "success" },
  { key: "outstanding", label: "Outstanding", tone: "warning" },
];

const getDownloadFilename = (headers, fallbackName) => {
  const contentDisposition = headers?.["content-disposition"] || headers?.["Content-Disposition"] || "";
  const encodedNameMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (encodedNameMatch?.[1]) {
    try {
      return decodeURIComponent(encodedNameMatch[1].trim());
    } catch (_error) {
      return encodedNameMatch[1].trim();
    }
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  const resolvedName = filenameMatch?.[1] || filenameMatch?.[2];
  return resolvedName ? resolvedName.trim() : fallbackName;
};

const normalizeTreatmentDecision = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "";
  return TREATMENT_DECISION_OPTIONS.some((option) => option.value === normalized) ? normalized : "";
};

const normalizeControlEffectiveness = (value) => {
  return normalizeControlEffectivenessKey(value, { blankKey: "unrated" }) || "unrated";
};

const normalizeActionPriority = (value) => {
  const normalized = String(value || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return "unassigned";
  if (["critical", "high", "medium", "low"].includes(normalized)) return normalized;
  return "unassigned";
};

const isActionCompleted = (value) => String(value || "").trim().toLowerCase() === "completed";

const normalizeStatus = (rawStatus) => {
  const raw = String(rawStatus || "").trim().toLowerCase();
  if (!raw || raw === "open") return "Draft";
  if (["monitoring", "review", "in review"].includes(raw)) return "In Review";
  if (raw === "approved") return "Approved";
  if (raw === "closed") return "Closed";
  if (raw === "draft") return "Draft";
  return String(rawStatus || "Draft");
};

const toDisplayDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleDateString();
};

const toDisplayDateTime = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return parsed.toLocaleString();
};

const toDisplayReviewFrequency = (value) => {
  const key = String(value || "").trim().toLowerCase();
  return REVIEW_FREQUENCY_LABELS[key] || value || "-";
};

const toAuditPlainText = (value) => {
  const text = richTextToPlainText(value);
  return text || "";
};

export default function RiskDetailsView({ riskId, onRiskUpdated, onOpenInPanel, mode = "page" }) {
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { items: effectivenessConfig } = useControlEffectivenessConfig();
  const riskIdNumber = Number(riskId);
  const [risk, setRisk] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [controlDrawerId, setControlDrawerId] = useState(null);
  const [actionDrawerId, setActionDrawerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [transitioning, setTransitioning] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);
  const [transitionReason, setTransitionReason] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreReason, setRestoreReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [editingDetails, setEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printPreviewVisible, setPrintPreviewVisible] = useState(false);
  const [downloadingPrintPdf, setDownloadingPrintPdf] = useState(false);
  const [selectedPrintFields, setSelectedPrintFields] = useState(DEFAULT_PRINT_FIELD_KEYS);
  const [printSubOptions, setPrintSubOptions] = useState(DEFAULT_PRINT_SUB_OPTIONS);
  const [printMatrixConfig, setPrintMatrixConfig] = useState(() => normalizeMatrixConfig(null));
  const [detailsDraft, setDetailsDraft] = useState({
    name: "",
    risk_code: "",
    category: "",
    subcategory: "",
    owner_id: "",
    editor_id: "",
    review_frequency: "",
    treatment_decision: "",
    description: "<p></p>",
    causes: "<p></p>",
    impact_implications: "<p></p>",
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryOptionsLoading, setCategoryOptionsLoading] = useState(false);
  const [usersOptions, setUsersOptions] = useState([]);
  const [usersOptionsLoading, setUsersOptionsLoading] = useState(false);
  const [linkControlsTrigger, setLinkControlsTrigger] = useState(0);
  const [linkedControlsRefreshKey, setLinkedControlsRefreshKey] = useState(0);
  const [createControlOpen, setCreateControlOpen] = useState(false);
  const [linkedControls, setLinkedControls] = useState([]);
  const [riskActions, setRiskActions] = useState([]);
  const riskRef = useRef(null);
  const getStatusCode = useCallback((err) => Number(err?.status || err?.response?.status || 0), []);
  const handleUnauthorized = useCallback(
    async (err) => {
      if (getStatusCode(err) !== 401) return false;
      setActionError("Session expired. Redirecting to login...");
      if (typeof logout === "function") {
        await logout();
      }
      return true;
    },
    [getStatusCode, logout]
  );

  const buildDetailsDraft = useCallback((sourceRisk) => ({
    name: sourceRisk?.name || "",
    risk_code: sourceRisk?.risk_code || "",
    category: sourceRisk?.category || "",
    subcategory: sourceRisk?.subcategory || "",
    owner_id: sourceRisk?.owner_id != null ? String(sourceRisk.owner_id) : "",
    editor_id: sourceRisk?.editor_id != null ? String(sourceRisk.editor_id) : "",
    review_frequency: sourceRisk?.review_frequency || "",
    treatment_decision: normalizeTreatmentDecision(sourceRisk?.treatment_decision),
    description: ensureRichTextHtml(sourceRisk?.description || ""),
    causes: ensureRichTextHtml(sourceRisk?.causes || ""),
    impact_implications: ensureRichTextHtml(sourceRisk?.impact_implications || ""),
  }), []);

  const loadRisk = useCallback(async () => {
    if (!token || !riskIdNumber) return;
    setLoading(true);
    setError(null);
    try {
      const [riskData, historyData] = await Promise.all([getRiskById(riskIdNumber), getRiskHistory(riskIdNumber)]);
      setRisk(riskData);
      riskRef.current = riskData;
      setAuditLog(Array.isArray(historyData) ? historyData : []);
      setActionError(null);
    } catch (err) {
      const redirected = await handleUnauthorized(err);
      if (redirected) return;
      setError(err?.response?.data?.detail || err?.message || "Unable to load risk details.");
    } finally {
      setLoading(false);
    }
  }, [token, riskIdNumber, handleUnauthorized]);

  useEffect(() => {
    loadRisk();
  }, [loadRisk]);

  const normalizedStatus = useMemo(() => normalizeStatus(risk?.status), [risk?.status]);
  useRegisterRecentView({
    itemType: "risk",
    itemId: risk?.id || riskIdNumber,
    title: risk?.name,
    secondaryText: risk?.risk_code || risk?.category || null,
    enabled: Boolean(risk?.id && risk?.name),
  });
  const isArchived = Boolean(risk?.archived_at);
  const isAdmin = isRole(user?.role, RoleEnum.ADMIN);
  const isManager = isRole(user?.role, RoleEnum.MANAGER);
  const hasRiskEditAccess = hasModuleAccess(user, "risks", "EDIT", { allowLegacyAdmin: false });
  const hasRiskManageAccess = hasModuleAccess(user, "risks", "MANAGE", { allowLegacyAdmin: false });
  const isOwner = useMemo(() => {
    if (!risk?.owner_id) return false;
    const ownerValue = String(risk.owner_id);
    return [String(user?.username || ""), String(user?.id || "")]
      .filter(Boolean)
      .includes(ownerValue);
  }, [risk?.owner_id, user?.id, user?.username]);
  const canEditAll = hasRiskEditAccess && (isAdmin || isManager);
  const canEditName = canEditAll && !isArchived;
  const canEditRiskCode = canEditAll && !isArchived;
  const canEditCategory = canEditAll && !isArchived;
  const canEditOwnerField = canEditAll && !isArchived;
  const canEditEditorField = canEditAll && !isArchived;
  const canEditSubcategory = canEditAll && !isArchived;
  const canEditReviewFrequency = !isArchived && hasRiskEditAccess && (canEditAll || isOwner);
  const canEditTreatmentDecision = canEditAll && !isArchived;
  const canEditNarrativeFields = !isArchived && hasRiskEditAccess && (canEditAll || isOwner);
  const canEditScores = canEditAll && !isArchived;
  const canEditDetails =
    canEditName ||
    canEditRiskCode ||
    canEditCategory ||
    canEditOwnerField ||
    canEditEditorField ||
    canEditSubcategory ||
    canEditReviewFrequency ||
    canEditTreatmentDecision ||
    canEditNarrativeFields;
  const canArchiveRisk = hasRiskManageAccess;
  const canDeleteRisk = hasRiskManageAccess && isAdmin;

  useEffect(() => {
    if (!risk || editingDetails) return;
    setDetailsDraft(buildDetailsDraft(risk));
  }, [risk, editingDetails, buildDetailsDraft]);

  const loadUsersOptions = useCallback(async () => {
    setUsersOptionsLoading(true);
    try {
      let users = [];
      const primaryLoader = isAdmin ? listAdminUsers : listUsers;
      const fallbackLoader = isAdmin ? listUsers : listAdminUsers;
      try {
        const res = await primaryLoader();
        users = Array.isArray(res?.data) ? res.data : [];
      } catch (primaryErr) {
        const statusCode = Number(primaryErr?.status || primaryErr?.response?.status || 0);
        if (statusCode !== 403 && statusCode !== 404) {
          throw primaryErr;
        }
        const fallback = await fallbackLoader();
        users = Array.isArray(fallback?.data) ? fallback.data : [];
      }
      const mapped = users
        .filter((item) => item?.username)
        .map((item) => ({
          value: String(item.username),
          id: String(item.id),
          username: String(item.username),
          label: item.email ? `${item.username} (${item.email})` : String(item.username),
        }));
      setUsersOptions(mapped);
    } catch (_err) {
      setUsersOptions([]);
    } finally {
      setUsersOptionsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!token) return;
    loadUsersOptions();
  }, [token, loadUsersOptions]);

  useEffect(() => {
    let active = true;

    getRiskMatrixConfig()
      .then((response) => {
        if (!active) return;
        setPrintMatrixConfig(normalizeMatrixConfig(response?.data || response));
      })
      .catch(() => {
        if (!active) return;
        setPrintMatrixConfig(normalizeMatrixConfig(null));
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!editingDetails) return;
    let active = true;

    const loadCategoryOptions = async () => {
      setCategoryOptionsLoading(true);
      try {
        const res = await listRiskCategories({ include_archived: false });
        const names = (res?.data || [])
          .map((item) => String(item?.name || "").trim())
          .filter(Boolean);
        if (!active) return;
        setCategoryOptions(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
      } catch (_err) {
        if (!active) return;
        setCategoryOptions([]);
      } finally {
        if (active) {
          setCategoryOptionsLoading(false);
        }
      }
    };

    loadCategoryOptions();
    return () => {
      active = false;
    };
  }, [editingDetails]);

  const patchRisk = useCallback(
    async (patch, { optimistic = true } = {}) => {
      if (!riskIdNumber) return null;
      let previousRisk = riskRef.current;
      if (optimistic && previousRisk) {
        const optimisticRisk = { ...previousRisk, ...patch };
        riskRef.current = optimisticRisk;
        setRisk(optimisticRisk);
      }
      try {
        await updateRisk(riskIdNumber, patch);
        const [refreshed, refreshedHistory] = await Promise.all([
          getRiskById(riskIdNumber),
          getRiskHistory(riskIdNumber),
        ]);
        riskRef.current = refreshed;
        setRisk(refreshed);
        setAuditLog(Array.isArray(refreshedHistory) ? refreshedHistory : []);
        onRiskUpdated && onRiskUpdated(refreshed);
        return refreshed;
      } catch (err) {
        if (optimistic && previousRisk) {
          riskRef.current = previousRisk;
          setRisk(previousRisk);
        }
        const redirected = await handleUnauthorized(err);
        if (redirected) return null;
        throw err;
      }
    },
    [onRiskUpdated, riskIdNumber, handleUnauthorized]
  );

  const workflowActions = useMemo(() => {
    if (isArchived) return [];
    const actions = [];
    if (normalizedStatus === "Draft" && hasRiskEditAccess && (canEditAll || isOwner)) {
      actions.push({
        to: "In Review",
        label: "Submit for Review",
        requiresReason: false,
      });
    }
    if (normalizedStatus === "In Review" && canEditAll) {
      const escalationRequired = Boolean(risk?.appetite_breach);
      actions.push({
        to: "Approved",
        label: "Approve",
        requiresReason: escalationRequired,
        reasonField: escalationRequired ? "appetite_escalation_reason" : null,
      });
    }
    if (normalizedStatus === "In Review" && hasRiskEditAccess && (canEditAll || isOwner)) {
      actions.push({
        to: "Draft",
        label: "Send Back to Draft",
        requiresReason: true,
        reasonField: "status_reason",
      });
    }
    if (normalizedStatus === "Approved" && canEditAll && hasRiskManageAccess) {
      actions.push({
        to: "Closed",
        label: "Close Risk",
        requiresReason: false,
      });
    }
    if (normalizedStatus === "Closed" && isAdmin) {
      actions.push({
        to: "Draft",
        label: "Reopen to Draft",
        requiresReason: true,
        reasonField: "status_reason",
      });
    }
    return actions;
  }, [normalizedStatus, canEditAll, hasRiskEditAccess, hasRiskManageAccess, isOwner, isAdmin, isArchived, risk?.appetite_breach]);

  const handleTransition = async (action) => {
    if (!risk?.id || !action) return;
    setActionError(null);
    if (action.requiresReason) {
      setPendingTransition(action);
      return;
    }
    setTransitioning(true);
    try {
      const updated = await patchRisk({ status: action.to }, { optimistic: true });
      if (!updated) return;
      setPendingTransition(null);
      setTransitionReason("");
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Unable to change status.");
    } finally {
      setTransitioning(false);
    }
  };

  const confirmTransition = async () => {
    if (!pendingTransition) return;
    setActionError(null);
    setTransitioning(true);
    try {
      const reason = transitionReason.trim();
      const payload = { status: pendingTransition.to };
      if (pendingTransition.reasonField && reason) {
        payload[pendingTransition.reasonField] = reason;
      }
      const updated = await patchRisk(payload, { optimistic: true });
      if (!updated) return;
      setPendingTransition(null);
      setTransitionReason("");
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Unable to change status.");
    } finally {
      setTransitioning(false);
    }
  };

  const handleArchive = async () => {
    if (!risk?.id) return;
    if (!archiveReason.trim()) {
      setActionError("Provide a reason for archiving this risk.");
      return;
    }
    setActionError(null);
    setTransitioning(true);
    try {
      await archiveRisk(risk.id, archiveReason.trim());
      setArchiveOpen(false);
      setArchiveReason("");
      await loadRisk();
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Failed to archive risk.");
    } finally {
      setTransitioning(false);
    }
  };

  const handleRestore = async () => {
    if (!risk?.id) return;
    setActionError(null);
    setTransitioning(true);
    try {
      await restoreRisk(risk.id, restoreReason.trim() || undefined);
      setRestoreOpen(false);
      setRestoreReason("");
      await loadRisk();
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Failed to restore risk.");
    } finally {
      setTransitioning(false);
    }
  };

  const handleDelete = async () => {
    if (!risk?.id) return;
    if (String(deleteConfirmation || "").trim().toUpperCase() !== "DELETE") {
      setActionError('Type "DELETE" to confirm hard deletion.');
      return;
    }
    setActionError(null);
    setTransitioning(true);
    try {
      await deleteRisk(
        risk.id,
        String(deleteConfirmation || "").trim(),
        String(deleteReason || "").trim() || undefined
      );
      setDeleteOpen(false);
      setDeleteReason("");
      setDeleteConfirmation("");
      window.dispatchEvent(new CustomEvent("riskDeleted", { detail: risk.id }));
      if (mode === "page") {
        navigate("/risks");
      }
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Failed to delete risk.");
    } finally {
      setTransitioning(false);
    }
  };

  const treatmentOverdue = useMemo(
    () =>
      !risk?.treatment_completed_at &&
      risk?.treatment_due_date &&
      new Date(risk.treatment_due_date).getTime() < Date.now(),
    [risk?.treatment_completed_at, risk?.treatment_due_date]
  );

  const ownerOptionById = useMemo(() => {
    const map = new Map();
    usersOptions.forEach((option) => {
      map.set(String(option.value || ""), option);
      map.set(String(option.id || ""), option);
      if (option.username) {
        map.set(String(option.username), option);
      }
    });
    return map;
  }, [usersOptions]);

  const ownerSelectValue = useMemo(() => {
    const current = String(detailsDraft.owner_id || "").trim();
    if (!current) return "";
    const matched = ownerOptionById.get(current);
    return matched ? String(matched.value || "") : current;
  }, [detailsDraft.owner_id, ownerOptionById]);

  const ownerDisplayValue = useMemo(() => {
    const current = String(risk?.owner_id || "").trim();
    if (!current) return "Unassigned";
    const matched = ownerOptionById.get(current);
    return matched?.label || current;
  }, [ownerOptionById, risk?.owner_id]);

  const editorSelectValue = useMemo(() => {
    const current = String(detailsDraft.editor_id || "").trim();
    if (!current) return "";
    const matched = ownerOptionById.get(current);
    if (!matched) return current;
    return String(matched.id || current);
  }, [detailsDraft.editor_id, ownerOptionById]);

  const editorDisplayValue = useMemo(() => {
    const current = String(risk?.editor_id || "").trim();
    if (!current) return "Unassigned";
    const matched = ownerOptionById.get(current);
    return matched?.label || current;
  }, [ownerOptionById, risk?.editor_id]);

  const controlsSummary = useMemo(() => {
    const counts = {
      ineffective: 0,
      needs_improvement: 0,
      effective: 0,
      unrated: 0,
    };
    (linkedControls || []).forEach((control) => {
      counts[normalizeControlEffectiveness(control?.effectiveness_rating)] += 1;
    });
    return counts;
  }, [linkedControls]);

  const controlEffectivenessSummaryItems = useMemo(
    () => [
      { key: "ineffective", label: getControlEffectivenessLabel(effectivenessConfig, "ineffective"), tone: CONTROL_EFFECTIVENESS_SUMMARY_TONES.ineffective },
      { key: "needs_improvement", label: getControlEffectivenessLabel(effectivenessConfig, "needs_improvement"), tone: CONTROL_EFFECTIVENESS_SUMMARY_TONES.needs_improvement },
      { key: "effective", label: getControlEffectivenessLabel(effectivenessConfig, "effective"), tone: CONTROL_EFFECTIVENESS_SUMMARY_TONES.effective },
      { key: "unrated", label: getControlEffectivenessLabel(effectivenessConfig, ""), tone: CONTROL_EFFECTIVENESS_SUMMARY_TONES.unrated },
    ],
    [effectivenessConfig]
  );

  const actionsPrioritySummary = useMemo(() => {
    const counts = {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
      unassigned: 0,
    };
    (riskActions || []).forEach((action) => {
      counts[normalizeActionPriority(action?.priority)] += 1;
    });
    return counts;
  }, [riskActions]);

  const actionsCompletionSummary = useMemo(() => {
    const counts = {
      completed: 0,
      outstanding: 0,
    };
    (riskActions || []).forEach((action) => {
      if (isActionCompleted(action?.status)) {
        counts.completed += 1;
      } else {
        counts.outstanding += 1;
      }
    });
    return counts;
  }, [riskActions]);

  const printFieldMap = useMemo(
    () =>
      buildRiskPrintFieldMap({
        risk: { ...risk, status: normalizedStatus },
        ownerLabel: ownerDisplayValue,
        auditLog,
        linkedControls,
        riskActions,
        matrixConfig: printMatrixConfig,
      }),
    [auditLog, linkedControls, normalizedStatus, ownerDisplayValue, printMatrixConfig, risk, riskActions]
  );

  const riskPrintPreview = useMemo(
    () => buildRiskPrintPreview(selectedPrintFields, printFieldMap, printSubOptions),
    [printFieldMap, printSubOptions, selectedPrintFields]
  );

  const riskPrintOverflow = useMemo(
    () => estimateRiskPrintOverflow(selectedPrintFields, printFieldMap, printSubOptions),
    [printFieldMap, printSubOptions, selectedPrintFields]
  );

  const requestClose = useUnsavedChanges(
    useDraftDirty(detailsDraft, editingDetails)
  );

  const handleStartEditDetails = () => {
    setActionError(null);
    setDetailsDraft(buildDetailsDraft(riskRef.current));
    setEditingDetails(true);
  };

  const handleCancelEditDetails = () => {
    setActionError(null);
    setDetailsDraft(buildDetailsDraft(riskRef.current));
    setEditingDetails(false);
  };

  const handleSaveDetails = async () => {
    if (!riskRef.current) return;
    const toOptionalText = (value) => {
      const trimmed = String(value || "").trim();
      return trimmed || null;
    };
    const toOptionalHtml = (value) => {
      const html = sanitizeRichTextHtml(value);
      return isRichTextEmpty(html) ? null : html;
    };

    const editableFields = {
      name: canEditName,
      risk_code: canEditRiskCode,
      category: canEditCategory,
      subcategory: canEditSubcategory,
      owner_id: canEditOwnerField,
      editor_id: canEditEditorField,
      review_frequency: canEditReviewFrequency,
      treatment_decision: canEditTreatmentDecision,
      description: canEditNarrativeFields,
      causes: canEditNarrativeFields,
      impact_implications: canEditNarrativeFields,
    };

    const normalizedDraft = {
      name: toOptionalText(detailsDraft.name),
      risk_code: toOptionalText(detailsDraft.risk_code),
      category: toOptionalText(detailsDraft.category),
      subcategory: toOptionalText(detailsDraft.subcategory),
      owner_id: toOptionalText(ownerSelectValue),
      editor_id: toOptionalText(editorSelectValue),
      review_frequency: toOptionalText(detailsDraft.review_frequency),
      treatment_decision: toOptionalText(detailsDraft.treatment_decision),
      description: toOptionalHtml(detailsDraft.description),
      causes: toOptionalHtml(detailsDraft.causes),
      impact_implications: toOptionalHtml(detailsDraft.impact_implications),
    };

    if (canEditName && !normalizedDraft.name) {
      setActionError("Risk name is required.");
      return;
    }
    if (canEditCategory && !normalizedDraft.category) {
      setActionError("Risk category is required.");
      return;
    }

    const current = buildDetailsDraft(riskRef.current);
    const normalizedCurrent = {
      name: toOptionalText(current.name),
      risk_code: toOptionalText(current.risk_code),
      category: toOptionalText(current.category),
      subcategory: toOptionalText(current.subcategory),
      owner_id: toOptionalText(current.owner_id),
      editor_id: toOptionalText(current.editor_id),
      review_frequency: toOptionalText(current.review_frequency),
      treatment_decision: toOptionalText(current.treatment_decision),
      description: toOptionalHtml(current.description),
      causes: toOptionalHtml(current.causes),
      impact_implications: toOptionalHtml(current.impact_implications),
    };

    const patch = {};
    Object.entries(normalizedDraft).forEach(([key, value]) => {
      if (!editableFields[key]) return;
      if ((normalizedCurrent[key] ?? null) !== (value ?? null)) {
        patch[key] = value;
      }
    });

    if (Object.keys(patch).length === 0) {
      setEditingDetails(false);
      return;
    }

    setActionError(null);
    setSavingDetails(true);
    try {
      const updated = await patchRisk(patch, { optimistic: true });
      if (!updated) return;
      setEditingDetails(false);
    } catch (err) {
      setActionError(err?.response?.data?.detail || err?.message || "Unable to save risk details.");
    } finally {
      setSavingDetails(false);
    }
  };

  const handleOpenFullPage = useCallback(() => {
    if (!risk?.id) return;
    navigate(`/risks/${risk.id}`);
  }, [navigate, risk?.id]);

  const handleOpenPrintModal = useCallback(() => {
    setSelectedPrintFields([...DEFAULT_PRINT_FIELD_KEYS]);
    setPrintSubOptions({ ...DEFAULT_PRINT_SUB_OPTIONS });
    setPrintPreviewVisible(false);
    setPrintModalOpen(true);
  }, []);

  const handleTogglePrintField = useCallback((fieldKey) => {
    setSelectedPrintFields((current) => {
      const next = current.includes(fieldKey)
        ? current.filter((key) => key !== fieldKey)
        : [...current, fieldKey];

      if (fieldKey === "linked_controls" && !next.includes("linked_controls")) {
        setPrintSubOptions((currentSubOptions) => ({
          ...currentSubOptions,
          linked_controls_summary: false,
        }));
      }
      if (fieldKey === "linked_actions" && !next.includes("linked_actions")) {
        setPrintSubOptions((currentSubOptions) => ({
          ...currentSubOptions,
          linked_actions_summary: false,
        }));
      }
      return next;
    });
  }, []);

  const handleTogglePrintSubOption = useCallback((optionKey) => {
    setPrintSubOptions((current) => ({
      ...current,
      [optionKey]: !current?.[optionKey],
    }));
  }, []);

  const handlePreviewPrint = useCallback(() => {
    setPrintPreviewVisible(true);
  }, []);

  const handlePrint = useCallback(() => {
    if (!risk?.id) return;
    setPrintPreviewVisible(true);
    setDownloadingPrintPdf(true);
    setActionError(null);

    exportRiskPdf(risk.id, {
      selected_fields: selectedPrintFields,
      sub_options: printSubOptions,
    })
      .then((response) => {
        const pdfBlob = response?.data instanceof Blob ? response.data : new Blob([response?.data ?? response], { type: "application/pdf" });
        const downloadName = getDownloadFilename(response?.headers, `risk-${risk.id}-one-page.pdf`);
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = downloadName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        setActionError(err?.response?.data?.detail || err?.message || "Unable to download risk PDF.");
      })
      .finally(() => {
        setDownloadingPrintPdf(false);
      });
  }, [printSubOptions, risk?.id, selectedPrintFields]);

  if (!token) {
    return <div style={pageStateStyle}>Not authenticated.</div>;
  }

  if (loading) {
    return <div style={pageStateStyle}>Loading risk details...</div>;
  }

  if (error) {
    return <div style={pageErrorStyle}>Error: {error}</div>;
  }

  if (!risk) {
    return <div style={pageStateStyle}>Risk not found.</div>;
  }

  return (
    <div style={detailsShellStyle}>
      <div style={stickyRiskNameBarStyle}>
        <h1 style={stickyRiskNameStyle}>{risk.name}</h1>
      </div>

      <CollapsibleSection title="Risk Overview" defaultOpen>
        <div style={headerContainerStyle}>
          <div style={metaRowStyle}>
            <StatusBadge value={normalizedStatus} />
            {isArchived && <StatusBadge value="Archived" />}
            <span style={metaPillStyle}>Owner: {ownerDisplayValue}</span>
            {risk.category && <span style={metaPillStyle}>{risk.category}</span>}
            {risk.subcategory && <span style={metaPillStyle}>{risk.subcategory}</span>}
          </div>
          <div style={actionsRowStyle}>
            {mode !== "page" && (
              <Button variant="secondary" onClick={handleOpenFullPage}>
                Open Full Page
              </Button>
            )}
            {mode === "page" && (
              <Button
                variant="secondary"
                onClick={() => navigate("/risks", { state: { openDrawerRiskId: risk.id } })}
              >
                Minimize to Side Panel
              </Button>
            )}
            {mode === "page" && (
              <PinButton
                itemType="risk"
                itemId={risk.id}
                summary={{
                  title: risk.name,
                  secondaryText: [risk.category, risk.subcategory].filter(Boolean).join(" | "),
                  status: normalizedStatus,
                  ownerDisplay: ownerDisplayValue,
                  updatedAt: risk.updated_at,
                }}
              />
            )}
            {mode === "page" && (
              <Button variant="secondary" onClick={handleOpenPrintModal}>
                Print risk to one page
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => setArchiveOpen(true)}
              disabled={!canArchiveRisk || isArchived}
            >
              Archive
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={!canDeleteRisk}
              title={!canDeleteRisk ? "Only admins can hard delete a risk." : undefined}
            >
              Delete
            </Button>
            {isArchived && canArchiveRisk && (
              <Button variant="secondary" onClick={() => setRestoreOpen(true)}>
                Restore
              </Button>
            )}
          </div>
        </div>
        <div style={{ ...sectionMutedTextStyle, marginTop: spacing.sm }}>
          Last updated: {toDisplayDateTime(risk.updated_at)}
        </div>
        <div style={sectionActionRowStyle}>
          {workflowActions.length === 0 && (
            <span style={sectionMutedTextStyle}>
              No workflow actions available for your role.
            </span>
          )}
          {workflowActions.map((action) => (
            <Button
              key={action.label}
              variant="secondary"
              onClick={() => handleTransition(action)}
              disabled={transitioning}
            >
              {action.label}
            </Button>
          ))}
        </div>
        {pendingTransition && (
          <div style={inlinePanelStyle}>
            <div style={{ fontSize: typography.size.sm, fontWeight: typography.weight.medium }}>
              {pendingTransition.reasonField === "appetite_escalation_reason"
                ? "Escalation reason is required before approving this appetite breach."
                : "Provide a reason for this transition."}
            </div>
            <Textarea
              rows={3}
              value={transitionReason}
              onChange={(event) => setTransitionReason(event.target.value)}
              placeholder="Enter reason..."
              style={inputStyle}
            />
            <div style={inlineActionsStyle}>
              <Button
                variant="primary"
                onClick={confirmTransition}
                disabled={transitioning || !transitionReason.trim()}
              >
                Confirm
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setPendingTransition(null);
                  setTransitionReason("");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {actionError && <div style={sectionErrorStyle}>{actionError}</div>}
      </CollapsibleSection>

      <ScoringPanel
        risk={risk}
        history={auditLog}
        canEditScores={canEditScores}
        onPatchRisk={patchRisk}
        reviewGuidance={
          normalizedStatus === "Approved" && !isArchived
            ? canEditScores
              ? {
                  mode: "editable",
                  title: "Reviewing this risk",
                  body:
                    "To review the risk, select the appropriate score from the risk matrix below, update the review details and next review date, then save your changes.",
                }
              : {
                  mode: "readonly",
                  title: "Reviewing this risk",
                  body:
                    "You can view the current risk matrix here. Editing and reviewing this risk requires write access to Risks and scoring permissions.",
                }
            : null
        }
        compactLayout={mode === "drawer"}
      />

      <CollapsibleSection title="Risk Summary" defaultOpen>
        <div style={sectionHeaderActionsStyle}>
          {canEditDetails && !editingDetails && (
            <Button variant="secondary" onClick={handleStartEditDetails}>
              Edit Details
            </Button>
          )}
          {canEditDetails && editingDetails && (
            <div style={inlineActionsStyle}>
              <Button variant="secondary" onClick={requestClose || handleCancelEditDetails} disabled={savingDetails}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSaveDetails} disabled={savingDetails}>
                {savingDetails ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </div>
        <div style={gridStyle}>
          <ReadItem label="Status" value={normalizedStatus} />
          <ReadItem label="Risk Code" value={risk.risk_code || "-"} />
          <ReadItem label="Owner" value={ownerDisplayValue} />
          <ReadItem label="Editor" value={editorDisplayValue} />
          <ReadItem label="Appetite Status" value={risk.appetite_status || (risk.appetite_breach ? "Exceeds Appetite" : "Within Appetite")} />
          <ReadItem
            label="Review Frequency"
            value={toDisplayReviewFrequency(editingDetails ? detailsDraft.review_frequency : risk.review_frequency)}
          />
        </div>
        {editingDetails && (
          <div style={gridStyle}>
            <EditItem label="Risk Name">
              <Input
                value={detailsDraft.name}
                onChange={(event) => setDetailsDraft((current) => ({ ...current, name: event.target.value }))}
                style={inputStyle}
                disabled={!canEditName}
              />
            </EditItem>
            <EditItem label="Risk Code">
              <Input
                value={detailsDraft.risk_code}
                onChange={(event) => setDetailsDraft((current) => ({ ...current, risk_code: event.target.value }))}
                style={inputStyle}
                disabled={!canEditRiskCode}
              />
            </EditItem>
            <EditItem label="Category">
              <Select
                value={detailsDraft.category}
                onChange={(event) => setDetailsDraft((current) => ({ ...current, category: event.target.value }))}
                style={inputStyle}
                disabled={!canEditCategory || categoryOptionsLoading}
              >
                <option value="">Select category</option>
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </EditItem>
            <EditItem label="Subcategory">
              <Input
                value={detailsDraft.subcategory}
                onChange={(event) => setDetailsDraft((current) => ({ ...current, subcategory: event.target.value }))}
                style={inputStyle}
                disabled={!canEditSubcategory}
              />
            </EditItem>
            <EditItem label="Owner">
              <Select
                value={ownerSelectValue}
                onChange={(event) =>
                  setDetailsDraft((current) => ({ ...current, owner_id: event.target.value }))
                }
                style={inputStyle}
                disabled={!canEditOwnerField || usersOptionsLoading}
              >
                <option value="">Unassigned</option>
                {usersOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {ownerSelectValue &&
                  !usersOptions.some((option) => option.value === ownerSelectValue) && (
                    <option value={ownerSelectValue}>{detailsDraft.owner_id}</option>
                  )}
              </Select>
            </EditItem>
            <EditItem label="Editor">
              <Select
                value={editorSelectValue}
                onChange={(event) =>
                  setDetailsDraft((current) => ({ ...current, editor_id: event.target.value }))
                }
                style={inputStyle}
                disabled={!canEditEditorField || usersOptionsLoading}
              >
                <option value="">Unassigned</option>
                {usersOptions.map((option) => (
                  <option key={`editor-${option.id}`} value={option.id}>
                    {option.label}
                  </option>
                ))}
                {editorSelectValue &&
                  !usersOptions.some((option) => String(option.id) === editorSelectValue) && (
                    <option value={editorSelectValue}>{detailsDraft.editor_id}</option>
                  )}
              </Select>
            </EditItem>
            <EditItem label="Review Frequency">
              <Select
                value={detailsDraft.review_frequency}
                onChange={(event) => setDetailsDraft((current) => ({ ...current, review_frequency: event.target.value }))}
                style={inputStyle}
                disabled={!canEditReviewFrequency}
              >
                <option value="">Not set</option>
                {REVIEW_FREQUENCY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </EditItem>
            <EditItem label="Treatment Decision">
              <Select
                value={detailsDraft.treatment_decision}
                onChange={(event) =>
                  setDetailsDraft((current) => ({ ...current, treatment_decision: event.target.value }))
                }
                style={inputStyle}
                disabled={!canEditTreatmentDecision}
              >
                <option value="">Not set</option>
                {TREATMENT_DECISION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </EditItem>
          </div>
        )}
        <div style={gridStyle}>
          {editingDetails ? (
            <>
              <EditBlockItem
                label="Description"
                value={detailsDraft.description}
                onChange={(value) => setDetailsDraft((current) => ({ ...current, description: value }))}
                disabled={!canEditNarrativeFields}
              />
              <EditBlockItem
                label="Causes"
                value={detailsDraft.causes}
                onChange={(value) => setDetailsDraft((current) => ({ ...current, causes: value }))}
                disabled={!canEditNarrativeFields}
              />
              <EditBlockItem
                label="Impact / Implications"
                value={detailsDraft.impact_implications}
                onChange={(value) => setDetailsDraft((current) => ({ ...current, impact_implications: value }))}
                disabled={!canEditNarrativeFields}
              />
            </>
          ) : (
            <>
              <RichTextBlock label="Description" value={risk.description} />
              <RichTextBlock label="Causes" value={risk.causes} />
              <RichTextBlock label="Impact / Implications" value={risk.impact_implications} />
            </>
          )}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Treatments & Controls"
        titleMeta={(
          <span style={sectionSummaryMetaStyle}>
            <SectionSummaryGroup
              label="Effectiveness"
              items={controlEffectivenessSummaryItems}
              counts={controlsSummary}
            />
          </span>
        )}
        defaultOpen
      >
        <div style={gridStyle}>
          <ReadItem label="Decision" value={risk.treatment_decision || "-"} />
          <ReadItem label="Treatment Owner" value={risk.treatment_owner || risk.owner_id || "-"} />
          <ReadItem label="Due Date" value={toDisplayDate(risk.treatment_due_date)} />
          <ReadItem label="Treatment Status" value={risk.treatment_status || "-"} />
        </div>
        {treatmentOverdue && (
          <div style={sectionErrorStyle}>
            Treatment is overdue.
          </div>
        )}
        <div style={sectionActionRowStyle}>
          <Button variant="secondary" onClick={() => setCreateControlOpen(true)}>
            Create Control
          </Button>
          <Button variant="secondary" onClick={() => setLinkControlsTrigger((current) => current + 1)}>
            Link Control
          </Button>
        </div>
        <div style={{ marginTop: spacing.md }}>
          <LinkedControlsPanel
            riskId={risk.id}
            refreshKey={linkedControlsRefreshKey}
            linkTrigger={linkControlsTrigger}
            users={usersOptions}
            formatDate={toDisplayDate}
            onOpenControl={(control) => setControlDrawerId(control?.id || null)}
            onControlsChange={setLinkedControls}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Actions"
        titleMeta={(
          <span style={sectionSummaryMetaStyle}>
            <SectionSummaryGroup
              label="Priority"
              items={ACTION_PRIORITY_SUMMARY_ITEMS}
              counts={actionsPrioritySummary}
            />
            <SectionSummaryGroup
              label="Status"
              items={ACTION_COMPLETION_SUMMARY_ITEMS}
              counts={actionsCompletionSummary}
            />
          </span>
        )}
        defaultOpen
      >
        <RiskActionsPanel
          riskId={risk.id}
          formatDate={toDisplayDate}
          onOpenAction={(action) => setActionDrawerId(action?.id || null)}
          onActionsChange={setRiskActions}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Evidence" defaultOpen>
        <RiskEvidence riskId={risk.id} />
      </CollapsibleSection>

      <CollapsibleSection title="Audit Timeline" defaultOpen>
        {auditLog.length === 0 && (
          <div style={auditEmptyTextStyle}>No audit entries.</div>
        )}
        {auditLog.length > 0 && (
          <div style={{ maxHeight: 260, overflowY: "auto", marginTop: spacing.sm }}>
            {auditLog.map((entry) => (
              <div key={entry.id} style={auditEntryStyle}>
                <div style={{ fontSize: typography.size.sm }}>
                  <strong>{entry.field || "update"}</strong>{" "}
                  <span style={auditEntryChangeStyle}>
                    {entry.from_value || "-"} -> {entry.to_value || "-"}
                  </span>
                </div>
                <div style={auditEntryMetaStyle}>
                  {entry.changed_by || "system"} - {toDisplayDateTime(entry.changed_at)}
                </div>
                {toAuditPlainText(entry.note) && (
                  <div
                    style={{
                      fontSize: typography.size.xs,
                      color: colors.textMuted,
                      marginTop: spacing.xs / 2,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {toAuditPlainText(entry.note)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      <ConfirmModal
        open={archiveOpen}
        title="Archive Risk"
        confirmLabel={transitioning ? "Archiving..." : "Archive"}
        value={archiveReason}
        onValueChange={setArchiveReason}
        onCancel={() => {
          setArchiveOpen(false);
          setArchiveReason("");
        }}
        onConfirm={handleArchive}
        confirmDisabled={transitioning || !archiveReason.trim()}
        helperText="Provide a reason for archive audit history."
      />

      <ConfirmModal
        open={restoreOpen}
        title="Restore Risk"
        confirmLabel={transitioning ? "Restoring..." : "Restore"}
        value={restoreReason}
        onValueChange={setRestoreReason}
        onCancel={() => {
          setRestoreOpen(false);
          setRestoreReason("");
        }}
        onConfirm={handleRestore}
        confirmDisabled={transitioning}
        helperText="Optional reason for restore."
      />

      {deleteOpen && (
        <ModalA11yWrapper
          open
          onClose={() => {
            if (transitioning) return;
            setDeleteOpen(false);
            setDeleteReason("");
            setDeleteConfirmation("");
          }}
        >
          <div style={modalStyle}>
            <div style={{ fontWeight: typography.weight.bold, fontSize: typography.size.lg }}>
              Delete Risk
            </div>
            <div style={{ fontSize: typography.size.sm, color: colors.danger }}>
              This permanently deletes the risk and cannot be undone.
            </div>
            <label style={fieldStyle}>
              <span style={{ fontSize: typography.size.sm }}>Reason (optional)</span>
              <Textarea
                rows={3}
                value={deleteReason}
                onChange={(event) => setDeleteReason(event.target.value)}
                placeholder="Why is this risk being deleted?"
                style={inputStyle}
              />
            </label>
            <label style={fieldStyle}>
              <span style={{ fontSize: typography.size.sm }}>
                Type <strong>DELETE</strong> to confirm
              </span>
              <Input
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                placeholder="DELETE"
                style={inputStyle}
              />
            </label>
            <div style={modalActionsStyle}>
              <Button
                variant="secondary"
                onClick={() => {
                  setDeleteOpen(false);
                  setDeleteReason("");
                  setDeleteConfirmation("");
                }}
                disabled={transitioning}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={transitioning || String(deleteConfirmation || "").trim().toUpperCase() !== "DELETE"}
              >
                {transitioning ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </ModalA11yWrapper>
      )}
      {printModalOpen && <RiskPrintStyles />}
      <RiskPrintModal
        open={printModalOpen}
        selectedKeys={selectedPrintFields}
        subOptions={printSubOptions}
        onToggleField={handleTogglePrintField}
        onToggleSubOption={handleTogglePrintSubOption}
        onCancel={() => {
          setPrintModalOpen(false);
          setPrintPreviewVisible(false);
        }}
        onPreview={handlePreviewPrint}
        onPrint={handlePrint}
        printing={downloadingPrintPdf}
        previewVisible={printPreviewVisible}
        preview={riskPrintPreview}
        overflow={riskPrintOverflow}
      />
      {printModalOpen && (
        <RiskPrintPreview
          preview={riskPrintPreview}
          root
          testId="risk-print-preview-root"
        />
      )}
      <ControlCreateModal
        open={createControlOpen}
        defaultRiskId={risk.id}
        onClose={() => setCreateControlOpen(false)}
        onCreated={() => {
          setLinkedControlsRefreshKey((current) => current + 1);
        }}
      />
      <ControlDetailDrawer controlId={controlDrawerId} onClose={() => setControlDrawerId(null)} />
      <ActionDetailDrawer actionId={actionDrawerId} onClose={() => setActionDrawerId(null)} />
    </div>
  );
}

function ConfirmModal({
  open,
  title,
  confirmLabel,
  value,
  onValueChange,
  onCancel,
  onConfirm,
  confirmDisabled,
  helperText,
}) {
  if (!open) return null;
  return (
    <ModalA11yWrapper open onClose={onCancel}>
      <div style={modalStyle}>
        <div style={{ fontWeight: typography.weight.bold, fontSize: typography.size.lg }}>{title}</div>
        <Textarea
          rows={4}
          value={value}
          onChange={(event) => onValueChange(event.target.value)}
          placeholder="Enter reason..."
          style={inputStyle}
        />
        {helperText && <div style={sectionMutedTextStyle}>{helperText}</div>}
        <div style={modalActionsStyle}>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={confirmDisabled}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </ModalA11yWrapper>
  );
}

function ReadItem({ label, value }) {
  return (
    <div style={{ fontSize: typography.size.sm }}>
      <div style={{ color: colors.textMuted, fontSize: typography.size.xs }}>{label}</div>
      <div style={{ color: colors.text, marginTop: spacing.xs / 2 }}>{value || "-"}</div>
    </div>
  );
}

function RichTextBlock({ label, value }) {
  const html = sanitizeRichTextHtml(ensureRichTextHtml(value));
  return (
    <div style={blockStyle}>
      <div style={{ color: colors.textMuted, fontSize: typography.size.xs, textTransform: "uppercase" }}>{label}</div>
      {isRichTextEmpty(html) ? (
        <div style={{ fontSize: typography.size.sm, color: colors.text }}>-</div>
      ) : (
        <div style={{ fontSize: typography.size.sm, color: colors.text }} dangerouslySetInnerHTML={{ __html: html }} />
      )}
    </div>
  );
}

function EditItem({ label, children }) {
  return (
    <label style={fieldStyle}>
      <span style={{ color: colors.textMuted, fontSize: typography.size.xs }}>{label}</span>
      {children}
    </label>
  );
}

function EditBlockItem({ label, value, onChange, disabled = false }) {
  return (
    <div style={blockStyle}>
      <span style={{ color: colors.textMuted, fontSize: typography.size.xs, textTransform: "uppercase" }}>{label}</span>
      <RichTextEditor
        value={value}
        onChange={onChange}
        disabled={disabled}
        minHeight={120}
      />
    </div>
  );
}

function SectionSummaryGroup({ label, items, counts }) {
  return (
    <span style={sectionSummaryGroupStyle}>
      <span style={sectionSummaryGroupLabelStyle}>{label}</span>
      {items.map((item) => (
        <SectionSummaryBadge
          key={item.key}
          label={item.label}
          count={counts?.[item.key] ?? 0}
          tone={item.tone}
        />
      ))}
    </span>
  );
}

function SectionSummaryBadge({ label, count, tone = "neutral" }) {
  return (
    <span
      style={{
        ...sectionSummaryBadgeStyle,
        ...(sectionSummaryBadgeToneStyles[tone] || sectionSummaryBadgeToneStyles.neutral),
      }}
    >
      {label} {count}
    </span>
  );
}

const headerContainerStyle = {
  display: "grid",
  gap: spacing.sm,
};

const metaRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const actionsRowStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  justifyContent: "flex-start",
  alignItems: "center",
};

const inlineActionsStyle = {
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const metaPillStyle = {
  padding: `${spacing.xs / 2}px ${spacing.sm}px`,
  borderRadius: radius.pill,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const sectionSummaryMetaStyle = {
  display: "flex",
  alignItems: "center",
  gap: spacing.sm,
  flexWrap: "wrap",
};

const sectionSummaryGroupStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: spacing.xs,
  flexWrap: "wrap",
};

const sectionSummaryGroupLabelStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  fontWeight: typography.weight.medium,
};

const sectionSummaryBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: radius.pill,
  padding: `${spacing.xs / 2}px ${spacing.sm}px`,
  fontSize: typography.size.xs,
  fontWeight: typography.weight.medium,
  border: "1px solid transparent",
  whiteSpace: "nowrap",
};

const sectionSummaryBadgeToneStyles = {
  danger: {
    background: "#fee2e2",
    color: "#b91c1c",
    borderColor: "#fecaca",
  },
  warning: {
    background: "#fef3c7",
    color: "#b45309",
    borderColor: "#fde68a",
  },
  success: {
    background: "#dcfce7",
    color: "#166534",
    borderColor: "#bbf7d0",
  },
  info: {
    background: "#dbeafe",
    color: "#1d4ed8",
    borderColor: "#bfdbfe",
  },
  neutral: {
    background: "#f3f4f6",
    color: "#4b5563",
    borderColor: "#e5e7eb",
  },
};

const inlinePanelStyle = {
  marginTop: spacing.md,
  padding: spacing.md,
  borderRadius: radius.md,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  boxShadow: shadows.xs,
  display: "grid",
  gap: spacing.sm,
};

const gridStyle = {
  display: "grid",
  gap: spacing.md,
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
};

const blockStyle = {
  borderRadius: radius.sm,
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  padding: spacing.md,
  display: "grid",
  gap: spacing.xs,
};

const auditEntryStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surfaceSubtle,
  padding: `${spacing.sm}px ${spacing.md}px`,
  marginBottom: spacing.sm,
};

const auditEntryChangeStyle = {
  color: colors.textMuted,
};

const auditEntryMetaStyle = {
  fontSize: typography.size.xs,
  color: colors.textMuted,
  marginTop: spacing.xs / 2,
};

const auditEmptyTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.sm,
};

const modalStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  boxShadow: shadows.md,
  padding: spacing.lg,
  width: 520,
  maxWidth: "90vw",
  display: "grid",
  gap: spacing.md,
};

const fieldStyle = {
  display: "grid",
  gap: spacing.xs,
};

const inputStyle = {
  borderRadius: radius.sm,
  border: `1px solid ${colors.border}`,
};

const detailsShellStyle = {
  display: "grid",
  gap: spacing.xl,
};

const stickyRiskNameBarStyle = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  background: colors.surface,
  borderBottom: `1px solid ${colors.border}`,
  padding: `${spacing.sm}px ${spacing.md}px`,
  borderRadius: radius.sm,
};

const stickyRiskNameStyle = {
  margin: 0,
  fontSize: typography.size.xl,
  color: colors.text,
};

const pageStateStyle = {
  padding: spacing.xl + spacing.lg,
  color: colors.textMuted,
};

const pageErrorStyle = {
  ...pageStateStyle,
  color: colors.danger,
};

const sectionMutedTextStyle = {
  color: colors.textMuted,
  fontSize: typography.size.xs,
};

const sectionActionRowStyle = {
  marginTop: spacing.md,
  display: "flex",
  gap: spacing.sm,
  flexWrap: "wrap",
  alignItems: "center",
};

const sectionHeaderActionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: spacing.md,
  flexWrap: "wrap",
  alignItems: "center",
};

const modalActionsStyle = {
  display: "flex",
  gap: spacing.sm,
  justifyContent: "flex-end",
  flexWrap: "wrap",
};

const sectionErrorStyle = {
  color: colors.danger,
  fontSize: typography.size.xs,
  marginTop: spacing.sm,
};

